import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount, useConnect, useNetwork, useSwitchNetwork } from "wagmi";

import { getWrongNetworkMessage } from "../../config/chainEnv";
import { SITE_TOKEN_SALE_HASH_LINK } from "../../constants/siteNavRoutes";
import {
  createCryptoOnrampSession,
  isStripeCryptoOnrampEnabledOnClient,
} from "../../services/cryptoOnrampApi";
import { TRDX_CHAIN_ID } from "../../services/web3/trdxContractConfig";

const ONRAMP_RETURN_FLAG = "treydora_onramp_return";
const FUND_ERROR = "Could not start ETH funding. Please try again.";

/**
 * Direct Stripe ETH funding flow (no purchase modal, no sale contract reads).
 */
export function useFundWalletEth() {
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors, isLoading: isConnecting } = useConnect();
  const { chain } = useNetwork();
  const { switchNetwork, isLoading: isSwitching } = useSwitchNetwork();

  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const resumeAfterConnectRef = useRef(false);

  const wrongNetwork = Boolean(isConnected && chain?.id && Number(chain.id) !== TRDX_CHAIN_ID);
  const busy = phase !== "idle" || isConnecting || isSwitching;

  const statusMessage =
    phase === "connecting" || isConnecting
      ? "Connecting wallet…"
      : phase === "switching" || isSwitching
        ? "Switching to Ethereum Mainnet…"
        : phase === "preparing"
          ? "Preparing Stripe ETH funding…"
          : phase === "redirecting"
            ? "Redirecting to Stripe…"
            : "";

  const redirectToStripe = useCallback(async (walletAddress) => {
    if (!isStripeCryptoOnrampEnabledOnClient()) {
      throw new Error(FUND_ERROR);
    }
    setPhase("preparing");
    const returnUrl = `${window.location.origin}${SITE_TOKEN_SALE_HASH_LINK}`;
    try {
      sessionStorage.setItem(ONRAMP_RETURN_FLAG, "1");
    } catch {
      /* noop */
    }
    const session = await createCryptoOnrampSession({
      walletAddress,
      returnUrl,
    });
    const url = String(session?.redirectUrl || "").trim();
    if (!url) {
      throw new Error(FUND_ERROR);
    }
    setPhase("redirecting");
    window.location.assign(url);
  }, []);

  const continueFundFlow = useCallback(
    async (walletAddress) => {
      if (!walletAddress) {
        setError(FUND_ERROR);
        setPhase("idle");
        return;
      }
      if (Number(chain?.id) !== TRDX_CHAIN_ID) {
        setPhase("switching");
        try {
          if (!switchNetwork) {
            setError(getWrongNetworkMessage(TRDX_CHAIN_ID));
            setPhase("idle");
            return;
          }
          await switchNetwork(TRDX_CHAIN_ID);
        } catch {
          setError(FUND_ERROR);
          setPhase("idle");
          return;
        }
      }
      try {
        await redirectToStripe(walletAddress);
      } catch {
        setError(FUND_ERROR);
        setPhase("idle");
      }
    },
    [chain?.id, redirectToStripe, switchNetwork]
  );

  const start = useCallback(async () => {
    setError("");
    if (!window?.ethereum) {
      setError(FUND_ERROR);
      return;
    }
    if (!isStripeCryptoOnrampEnabledOnClient()) {
      setError(FUND_ERROR);
      return;
    }

    if (!isConnected || !address) {
      setPhase("connecting");
      resumeAfterConnectRef.current = true;
      const list = Array.isArray(connectors) ? connectors : [];
      const connector = list.find((c) => c?.ready) || list[0];
      if (!connector) {
        setError(FUND_ERROR);
        setPhase("idle");
        resumeAfterConnectRef.current = false;
        return;
      }
      try {
        const result = await connectAsync({ connector });
        const connectedAddress = result?.account || address;
        resumeAfterConnectRef.current = false;
        await continueFundFlow(connectedAddress);
      } catch {
        setError(FUND_ERROR);
        setPhase("idle");
        resumeAfterConnectRef.current = false;
      }
      return;
    }

    await continueFundFlow(address);
  }, [address, connectAsync, connectors, continueFundFlow, isConnected]);

  useEffect(() => {
    if (!resumeAfterConnectRef.current || !isConnected || !address) return;
    resumeAfterConnectRef.current = false;
    continueFundFlow(address);
  }, [isConnected, address, continueFundFlow]);

  return {
    start,
    busy,
    statusMessage,
    error,
    wrongNetwork,
  };
}
